<template>
  <div>
    <div class="row filesControl" v-if="labelPosition === 'left'">
      <div class="col-md-12">
        <label :for="control.name + '_gui_control'" class="form-control-label" :class="{'bold': control.labelBold, 'italic': control.labelItalic, 'underline': control.labelUnderline}">{{control.label}}</label>
      </div>
      <div class="col-md-12">
        <div class="editor">
          <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
            <div class="menubar">
              <span class="menubar__button" :class="{ 'is-active': isActive.bold() }" @click="commands.bold">
                <icon name="bold" />
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.italic() }" @click="commands.italic">
                <icon name="italic" />
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.strike() }" @click="commands.strike">
                <icon name="strike" />
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.underline() }" @click="commands.underline">
                <icon name="underline" />
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.code() }" @click="commands.code">
                <icon name="code" />
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.paragraph() }" @click="commands.paragraph">
                <icon name="paragraph" />
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.heading({ level: 1 }) }" @click="commands.heading({ level: 1 })">
                H1
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.heading({ level: 2 }) }" @click="commands.heading({ level: 2 })">
                H2
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.heading({ level: 3 }) }" @click="commands.heading({ level: 3 })">
                H3
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.bullet_list() }" @click="commands.bullet_list">
                <icon name="ul" />
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.ordered_list() }" @click="commands.ordered_list">
                <icon name="ol" />
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.blockquote() }" @click="commands.blockquote">
                <icon name="quote" />
              </span>
              <span class="menubar__button" :class="{ 'is-active': isActive.code_block() }" @click="commands.code_block">
                <icon name="code" />
              </span>
              <span class="menubar__button" @click="commands.horizontal_rule">
                <icon name="hr" />
              </span>
              <span class="menubar__button" @click="commands.undo">
                <icon name="undo" />
              </span>
              <span class="menubar__button" @click="commands.redo">
                <icon name="redo" />
              </span>
              <span
                class="menubar__button"
                @click="commands.createTable({rowsCount: 3, colsCount: 3, withHeaderRow: false })"
              >
                <icon name="table" />
              </span>
              <span v-if="isActive.table()">
                <span
                  class="menubar__button"
                  @click="commands.deleteTable"
                >
                  <icon name="delete_table" />
                </span>
                <span
                  class="menubar__button"
                  @click="commands.addColumnBefore"
                >
                  <icon name="add_col_before" />
                </span>
                <span
                  class="menubar__button"
                  @click="commands.addColumnAfter"
                >
                  <icon name="add_col_after" />
                </span>
                <span
                  class="menubar__button"
                  @click="commands.deleteColumn"
                >
                  <icon name="delete_col" />
                </span>
                <span
                  class="menubar__button"
                  @click="commands.addRowBefore"
                >
                  <icon name="add_row_before" />
                </span>
                <span
                  class="menubar__button"
                  @click="commands.addRowAfter"
                >
                  <icon name="add_row_after" />
                </span>
                <span
                  class="menubar__button"
                  @click="commands.deleteRow"
                >
                  <icon name="delete_row" />
                </span>
                <span
                  class="menubar__button"
                  @click="commands.toggleCellMerge"
                >
                  <icon name="combine_cells" />
                </span>
              </span>
            </div>
          </editor-menu-bar>
          <editor-content class="editor__content" :editor="editor" onUpdate="onChange" />
        </div>
        <div class="suggestion-list" v-show="showSuggestions" ref="suggestions">
          <template v-if="hasResults">
            <div v-for="(user, index) in filteredUsers" :key="user.id" class="suggestion-list__item" :class="{ 'is-selected': navigatedUserIndex === index }" @click="selectUser(user)">
              {{ user.name }}
            </div>
          </template>
          <div v-else class="suggestion-list__item is-empty">
            No users found
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Fuse from 'fuse.js'
import tippy, { sticky } from 'tippy.js'
import Icon from './GUITextEditorIcon'
import db from '@/services/db'
import { mapState } from 'vuex'
import edjsHTML from 'editorjs-html'
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  Mention,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Link,
  Table,
  TableHeader,
  TableCell,
  TableRow,
  Strike,
  Underline,
  History,
} from 'tiptap-extensions'
export default {
  components: {
    Icon,
    EditorContent,
    EditorMenuBar,
  },
  props: ["control", "labelPosition"],
  data() {
    return {
      editor: new Editor({
        extensions: [
          new Mention({
            // a list of all suggested items
            items: () => [
              { id: 1, name: 'Philipp Kühn' },
              { id: 2, name: 'Hans Pagel' },
              { id: 3, name: 'Kris Siepert' },
              { id: 4, name: 'Justin Schueler' },
            ],
            // is called when a suggestion starts
            onEnter: ({
              items, query, range, command, virtualNode,
            }) => {
              this.query = query
              this.filteredUsers = items
              this.suggestionRange = range
              this.renderPopup(virtualNode)
              // we save the command for inserting a selected mention
              // this allows us to call it inside of our custom popup
              // via keyboard navigation and on click
              this.insertMention = command
            },
            // is called when a suggestion has changed
            onChange: ({
              items, query, range, virtualNode,
            }) => {
              this.query = query
              this.filteredUsers = items
              this.suggestionRange = range
              this.navigatedUserIndex = 0
              this.renderPopup(virtualNode)
            },
            // is called when a suggestion is cancelled
            onExit: () => {
              // reset all saved values
              this.query = null
              this.filteredUsers = []
              this.suggestionRange = null
              this.navigatedUserIndex = 0
              this.destroyPopup()
            },
            // is called on every keyDown event while a suggestion is active
            onKeyDown: ({ event }) => {
              if (event.key === 'ArrowUp') {
                this.upHandler()
                return true
              }
              if (event.key === 'ArrowDown') {
                this.downHandler()
                return true
              }
              if (event.key === 'Enter') {
                this.enterHandler()
                return true
              }
              return false
            },
            // is called when a suggestion has changed
            // this function is optional because there is basic filtering built-in
            // you can overwrite it if you prefer your own filtering
            // in this example we use fuse.js with support for fuzzy search
            onFilter: (items, query) => {
              if (!query) {
                return items
              }
              const fuse = new Fuse(items, {
                threshold: 0.2,
                keys: ['name'],
              })
              return fuse.search(query)
            },
          }),
          new Blockquote(),
          new BulletList(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Link(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History(),
          new Table({ resizable: true }),
          new TableHeader(),
          new TableCell(),
          new TableRow(),
        ],
        onBlur: ({ event, state }) => {
          const data = {
            options: this.options,
            rowKey: this.rowKey,
            cellKey: this.cellKey,
            value: this.editor.getHTML(),
          }
          db.dataGrid.data.saveCellVal(data)
        },
        content: this.control.value,
      }),
      query: null,
      suggestionRange: null,
      filteredUsers: [],
      navigatedUserIndex: 0,
      insertMention: () => {},
    }
  },
  computed: {
    ...mapState(['dataGrid_options']),
    hasResults() {
      return this.filteredUsers.length
    },
    showSuggestions() {
      return this.query || this.hasResults
    },
    lookUpTableDisplayColumn() {
      return this.dataGrid_options[this.lookUpTable].displayColumn
    },
  },
  methods: {
    // navigate to the previous item
    // if it's the first item, navigate to the last one
    upHandler() {
      this.navigatedUserIndex = ((this.navigatedUserIndex + this.filteredUsers.length) - 1) % this.filteredUsers.length
    },
    // navigate to the next item
    // if it's the last item, navigate to the first one
    downHandler() {
      this.navigatedUserIndex = (this.navigatedUserIndex + 1) % this.filteredUsers.length
    },
    enterHandler() {
      const user = this.filteredUsers[this.navigatedUserIndex]
      if (user) {
        this.selectUser(user)
      }
    },
    // we have to replace our suggestion text with a mention
    // so it's important to pass also the position of your suggestion text
    selectUser(user) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user.id,
          label: user.name,
        },
      })
      this.editor.focus()
    },
    // renders a popup with suggestions
    // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
    renderPopup(node) {
      if (this.popup) {
        return
      }
      // ref: https://atomiks.github.io/tippyjs/v6/all-props/
      this.popup = tippy('.page', {
        getReferenceClientRect: node.getBoundingClientRect,
        appendTo: () => document.body,
        interactive: true,
        sticky: true, // make sure position of tippy is updated when content changes
        plugins: [sticky],
        content: this.$refs.suggestions,
        trigger: 'mouseenter', // manual
        showOnCreate: true,
        theme: 'dark',
        placement: 'top-start',
        inertia: true,
        duration: [400, 200],
      })
    },
    destroyPopup() {
      if (this.popup) {
        // this.popup[0].destroy()
        this.popup = null
      }
    },
  },
  created() {
    this.$nextTick(() => {
      const formBuilderInst = this.$instByClass(this, 'form-builder-root')
      this.gridId = formBuilderInst.gridId
      this.rowKey = formBuilderInst.rowKey
      this.cellKey = this.control.propName
      this.options = this.dataGrid_options[this.gridId]
    })
  },
  beforeDestroy() {
    this.destroyPopup()
  },
}
</script>

<style>
.mention {
  font-size: 0.8rem;
  font-weight: bold;
  border-radius: 5px;
  padding: 0.2rem 0.5rem;
  white-space: nowrap;
}
.suggestion-list {
  padding: 0.2rem;
  font-size: 0.8rem;
  font-weight: bold;
  background: black;
  color: white;
  border-radius: 4px;
  width: fit-content;
  margin-top: 10px;
  margin-left: 15px;
  position: relative;
}
 .suggestion-list__no-results {
  padding: 0.2rem 0.5rem;
}
 .suggestion-list__item {
  border-radius: 5px;
  padding: 0.2rem 0.5rem;
  margin-bottom: 0.2rem;
  cursor: pointer;
}
 .suggestion-list__item:last-child {
  margin-bottom: 0;
}
 .suggestion-list__item.is-empty {
  opacity: 0.5;
}
.tippy-box[data-theme~=dark] {
  padding: 0;
  font-size: 1rem;
  text-align: inherit;
  border-radius: 5px;
}
.editor {
  font-size: 1rem;
  line-height: 1.5;
  display: block;
  width: 100%;
  padding: .625rem .75rem;
  transition: all .15s cubic-bezier(.68, -.55, .265, 1.55);
  color: #8898aa;
  border: 1px solid #dee2e6;
  border-radius: .25rem;
  background-color: #fff;
  background-clip: padding-box;
  box-shadow: 0 3px 2px rgba(233, 236, 239, .05);
}
.menubar__button {
  cursor: pointer;
  margin: 0 5px;
  font-size: 12px;
}
.ProseMirror:focus {
  outline: none;
}
.tableWrapper {
  margin: 1em 0;
  overflow-x: auto;
}
.tableWrapper table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0;
  overflow: hidden;
}
.tableWrapper table td, .editor__content table th {
  min-width: 1em;
  border: 2px solid #ddd;
  padding: 3px 5px;
  vertical-align: top;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  position: relative;
}
.tableWrapper td>*, .editor__content table th>* {
  margin-bottom: 0;
}
</style>